import CircleIcon from "@mui/icons-material/Circle";
import { List, ListItem, ListItemText, useTheme } from "@mui/material";
import { PermissionsKeys } from "components/permissions/permission-keys";
import { LegacyProductKeys } from "components/permissions/permissions";
import { BatchConfig } from "./models";

export const premiumAdditionalParameters = [
  "solarPanelBundle",
  "buildings",
  "parcelBundle",
  "propertyBundle",
];

export const batchPermissionKeys = [
  PermissionsKeys.batchCreate,
  PermissionsKeys.batchRead,
  PermissionsKeys.batchUpdate,
  PermissionsKeys.batchDelete,
];
export const premiumBatchPermissionKeys = [
  PermissionsKeys.premiumDataCreate,
  PermissionsKeys.premiumDataRead,
  PermissionsKeys.premiumDataUpdate,
  PermissionsKeys.premiumDataDelete,
  ...batchPermissionKeys,
];

// buildings, parcelBundle, propertyBundle, solarPanelBundle
export const isBatchPremium = (batchConfig: BatchConfig) => {
  // TODO DS-5157: This should be defined somewhere else

  if (
    batchConfig.parameters === undefined ||
    batchConfig.parameters.additionalProperties === undefined
  ) {
    return false;
  }

  const isPremium = batchConfig.parameters.additionalProperties
    .map((additionalProperty: string) =>
      premiumAdditionalParameters.includes(additionalProperty)
    )
    .some(Boolean);

  return isPremium;
};

export interface BatchRow {
  id: string;
  displayName: string;
  batchId: string;
  rowCount: number | undefined;
  creditCost: number | undefined;
  startDate: number;
  isPremium: boolean;
  status: string;
  progress: number;
  downloadPath: string | undefined;
  subscriptionProductKey: string | undefined;
  batchConfig: BatchConfig;
}

const ProcessBatchRow = (batchConfig: BatchConfig): BatchRow => {
  let cost = batchConfig.batchInfo.estimatedCreditCost;
  if (batchConfig.batchInfo.actualCreditCost) {
    cost = batchConfig.batchInfo.actualCreditCost;
  }

  let progress = 0;
  if (batchConfig.batchInfo.progress) {
    if (batchConfig.status === "Running") {
      // In progress
      progress = +batchConfig.batchInfo.progress
        .replace("progress: ~", "")
        .replace(/%.*/, "");
    } else {
      // Finished
      progress = +batchConfig.batchInfo.progress
        .replace("progress: ", "")
        .replace("%", "");
    }
  }

  return {
    id: batchConfig.batchInfo.batchId,
    displayName: batchConfig.displayName,
    batchId: batchConfig.batchInfo.batchId,
    rowCount: batchConfig.batchInfo.totalRows,
    creditCost: cost,
    startDate: new Date(batchConfig.batchInfo.createdAt + "+00:00").getTime(), // Jank but lets us specify UTC timezone
    isPremium: isBatchPremium(batchConfig),
    status: batchConfig.status,
    progress: progress,
    downloadPath: batchConfig.batchInfo.downloadPath,
    subscriptionProductKey: batchConfig.subscriptionProductKey,
    batchConfig: batchConfig,
  };
};

const isFailureStatus = (status: string) => {
  if (status == "Cancelled" || status == "Failed") {
    return true;
  }

  return false;
};

const statusColour = (status: string) => {
  switch (status) {
    case "Completed":
      return "success";
    case "Failed":
      return "error";
    case "Running":
      return "info";
    case "Draft":
      return "secondary";
    case "Cancelled":
      return "warning";
    default: {
      return "info";
    }
  }
};

const GEOCODER = "Geocoder";

const batchOutputReverseGeocode = {
  output: [
    {
      name: "Address Properties - Address Id",
      property: "AddressProperties",
      attributeName: "addressId",
      source: "$.features[0].properties.addressId",
    },
    {
      name: "Address Properties - Formatted Address",
      property: "AddressProperties",
      attributeName: "formattedAddress",
      source: "$.features[0].properties.formattedAddress",
    },
    {
      name: "Address Properties - Dataset",
      property: "AddressProperties",
      attributeName: "dataset",
      source: "$.features[0].properties.dataset",
    },
    {
      name: "Geometry - Longitude",
      property: "Geometry",
      attributeName: "longitude",
      source: "$.features[0].geometry.coordinates[0]",
    },
    {
      name: "Geometry - Latitude",
      property: "Geometry",
      attributeName: "latitude",
      source: "$.features[0].geometry.coordinates[1]",
    },

    {
      name: "Reverse Geocoder Feature - MatchType",
      property: "matchingMetadataReverseGeocode",
      attributeName: "matchType",
      source: "$.features[0].matchType",
    },
    {
      name: "Reverse Geocoder Feature - Match Distance",
      property: "matchingMetadataReverseGeocode",
      attributeName: "matchDistance",
      source: "$.features[0].matchDistance",
    },
    {
      name: "Reverse Geocoder Feature - Match Method",
      property: "matchingMetadataReverseGeocode",
      attributeName: "matchMethod",
      source: "$.features[0].matchMethod",
    },
  ],
  properties: {
    AddressProperties: {
      addressId: true,
      formattedAddress: true,
      dataset: true,
    },
    matchingMetadataReverseGeocode: {
      matchType: true,
      matchDistance: true,
      matchMethod: true,
    },
    Geometry: {
      latitude: true,
      longitude: true,
    },
  },
};

const batchOutputGeocode = {
  output: [
    {
      name: "Address Properties - Address Id",
      property: "AddressProperties",
      attributeName: "addressId",
      source: "$.features[0].properties.addressId",
    },
    {
      name: "Address Properties - Formatted Address",
      property: "AddressProperties",
      attributeName: "formattedAddress",
      source: "$.features[0].properties.formattedAddress",
    },
    {
      name: "Address Properties - Dataset",
      property: "AddressProperties",
      attributeName: "dataset",
      source: "$.features[0].properties.dataset",
    },
    {
      name: "Geocoder Feature - MatchType",
      property: "matchingMetadataGeocode",
      attributeName: "matchType",
      source: "$.features[0].matchType",
    },
    {
      name: "Geocoder Feature - Match Quality",
      property: "matchingMetadataGeocode",
      attributeName: "matchQuality",
      source: "$.features[0].matchQuality",
    },
    {
      name: "Geocoder Feature - Match Score",
      property: "matchingMetadataGeocode",
      attributeName: "matchScore",
      source: "$.features[0].matchScore",
    },
    {
      name: "Geometry - Longitude",
      property: "Geometry",
      attributeName: "longitude",
      source: "$.features[0].geometry.coordinates[0]",
    },
    {
      name: "Geometry - Latitude",
      property: "Geometry",
      attributeName: "latitude",
      source: "$.features[0].geometry.coordinates[1]",
    },
  ],
  properties: {
    AddressProperties: {
      addressId: true,
      formattedAddress: true,
      dataset: true,
    },
    matchingMetadataGeocode: {
      matchType: true,
      matchQuality: true,
      matchScore: true,
    },
    matchingMetadataReverseGeocode: {
      matchType: true,
      matchDistance: true,
    },
    Geometry: {
      latitude: true,
      longitude: true,
    },
  },
};

const SelectedNamesList = ({ selectedNames }: any) => {
  const theme = useTheme();

  return (
    <List sx={{ paddingTop: 0, paddingBottom: 0 }}>
      {selectedNames.slice(0, 3).map((item: any, index: number) => (
        <ListItem
          sx={{
            paddingTop: 0,
            paddingBottom: 0,
            border: 0,
            marginBottom: -1,
          }}
          key={index}
        >
          <CircleIcon
            fontSize="small"
            sx={{
              marginRight: "8px",
              fontSize: "5px",
              color: theme.palette.text.secondary,
            }}
          />
          <ListItemText
            sx={{ color: theme.palette.text.secondary }}
            primary={` ${item}`}
          />
        </ListItem>
      ))}
      <ListItem sx={{ color: theme.palette.text.secondary }}>
        {selectedNames.length > 3 ? (
          <b> +{selectedNames.length - 3} more... </b>
        ) : (
          <></>
        )}
      </ListItem>
    </List>
  );
};

const isPermittedLegacy = (subscriptionProductKey: string, status: string) => {
  return (
    (subscriptionProductKey === undefined ||
      LegacyProductKeys.includes(subscriptionProductKey)) &&
    status.toLocaleUpperCase() === "COMPLETED"
  );
};

export {
  batchOutputGeocode,
  batchOutputReverseGeocode,
  GEOCODER,
  isFailureStatus,
  isPermittedLegacy,
  ProcessBatchRow,
  SelectedNamesList,
  statusColour,
};
